import { Countdown } from 'components/countdown';
import Button from 'components/ui/button';
import { useAuth } from 'context/AuthContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  getTaskCycles,
  getPlayerInfo,
  getTasks,
  getTasksManager,
  getVacationManager,
  getServerTime,
  getTasksPendingApprovalParticipants,
  getTotalTasks,
} from 'services/dashboard.service';
import { GetTaskLevelLabel, GetTaskStatusLabel } from 'utils/strings';

import ImgFeedback from 'assets/img/comportamento/bg-missoes-not-found.png';
import { User, VacationManagerInterface } from 'types/user';
import VacationManagerModal from 'components/vacation-manager-modal';
import { useModal } from 'context/ModalContext';
import { getStorageItem, removeStorageItem, setStorageItem } from 'utils/storage';
import { AiOutlineWarning } from 'react-icons/ai';
import Tippy from '@tippyjs/react';
import { BiInfoCircle } from 'react-icons/bi';
import { findLateralMenagers } from 'services/profile.service';
import SearchGestor from 'pages/MinhaEquipe/components/SearchGestor';
import {
  isAppreciationAreaVisible,
  isSubstituteWithoutOwnLevel,
  isSubstitutoComNivel,
  showsBothHomeAreas,
} from 'pages/Dashboard/taskCyclesVisibility';
import { UserNeedingApprovalModal } from 'components/user-needing-approval-modal';
import { TasksPendingApprovalParticipantsModal } from 'components/tasks-pending-approval-participants-modal';
import { LuBell } from 'react-icons/lu';
import { getMonthNameFromString, getTaskStep } from 'utils/helpers';
import ProgressBar from 'components/ui/progress-bar';
import { format, isFuture, isPast, isWithinInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MdOutlineLock } from 'react-icons/md';
import { IoIosWarning } from 'react-icons/io';

import { usePendingAppreciation } from '../usePendingAppreciation';

interface Props {
  taskManager: any[];
  hasPartialTask: boolean | undefined;
}

// Chamada de pendência do gestor. Com a contagem do aggregate mostra o volume — era
// justamente ele que a home escondia, exibindo "não há nada" a quem tinha centenas.
// Sem contagem (aggregate indisponível), mantém o texto genérico anterior.
function getPendingAppreciationLabel(count: number | null) {
  if (count === null || count <= 0) return 'Você tem Realizações Destaque esperando serem apreciadas!';
  if (count === 1) return 'Você tem 1 Realização Destaque esperando ser apreciada!';

  return `Você tem ${count} Realizações Destaque esperando serem apreciadas!`;
}

export function TaskCycles({ taskManager, hasPartialTask }: Props) {
  const navigate = useNavigate();
  const { user, userInfo, cyclesExtended, cycleSelected } = useAuth();
  const { showModal, closeModal } = useModal();
  const [canRegister, setCanRegister] = useState(true);
  const [predefinedAgreementType, setPredefinedAgreementType] = useState<boolean | undefined>();

  // const [tasks, setTasks] = useState<any>();
  const [currentTaskCycle, setCurrentTaskCycle] = useState<any>();
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [taskCycles, setTaskCycles] = useState<any>();
  const [myTasks, setMyTasks] = useState<any[]>();
  const [totalTask, setTotalTask] = useState<any>();
  const [participationTasks, setParticipationTasks] = useState<any[]>();
  const [vacationManagers, setVacationManagers] = useState<VacationManagerInterface[]>();
  const [evaluateTasks, setEvaluateTasks] = useState<any[]>();
  const [playerInfo, setPlayerInfo] = useState<any>();
  const [cycleActive, setCycleActive] = useState<string>();
  const [evaluateCycleActive, setEvaluateCycleActive] = useState<string>();
  const [lateralManagers, setLateralManagers] = useState<User[]>();
  const [showModalLateral, setShowModalLateral] = useState(false);
  const [isExperied, setIsExperied] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [loadingLateralManagers, setLoadingLateralManagers] = useState(false);

  const [tasksPendingApproval, setTasksPendingApproval] = useState<any[]>();

  const [realDatetime, setRealDatetime] = useState<number>(0);

  useEffect(() => {
    let interv: any = undefined;

    getServerTime().then(timestamp => {
      setRealDatetime(timestamp);

      interv = window.setInterval(() => {
        setRealDatetime(prevstate => prevstate + 1000);
      }, 1000);
    });

    return () => clearInterval(interv);
  }, []);

  // O ciclo selecionado precisa ser o corrente: o aggregate de elegibilidade resolve
  // sempre o ciclo `open`, então num ciclo passado a contagem não descreveria a tela.
  const selectedCycleIsCurrent = useMemo(
    () => cyclesExtended?.find(x => x._id === cycleSelected?._id)?.status === 'open',
    [cycleSelected, cyclesExtended],
  );

  // Pendências pelo aggregate canônico `apreciacao_flow_eligibility` — a MESMA fonte que
  // o gate de /minha-equipe/validar-apreciar-realizacao usa, e que já valida a janela de
  // cada realização por nível (Validação/N1/N2/Pit Stop/N3), as duplas MAD e a
  // lateralidade. O cache de sessão é COMPARTILHADO com o gate, então home → apreciar é
  // uma requisição só; o hook revalida sozinho quando a aba volta ao foco.
  const pendingApreciacao = usePendingAppreciation({
    playerId: user?._id,
    userInfo,
    currentCycle: selectedCycleIsCurrent,
  });

  const activeCycleObj = useMemo(() => taskCycles?.find((item: any) => item._id === cycleActive), [cycleActive, taskCycles]);

  const evaluateActiveCycleObj = useMemo(
    () => taskCycles?.find((item: any) => item._id === evaluateCycleActive),
    [evaluateCycleActive, taskCycles],
  );

  const taskPerCycle: any = useMemo(() => {
    if (!Array.isArray(myTasks) || !taskCycles?.length || !cycleActive) return null;

    const cycle = taskCycles.find((cycle: any) => cycle._id === cycleActive);

    let _task = myTasks;

    if (
      (!myTasks || !myTasks?.length || myTasks?.length < 1) &&
      participationTasks &&
      participationTasks?.length &&
      participationTasks?.length > 0
    ) {
      _task = participationTasks;
    }

    const tasks = _task.filter(
      item => item.created >= cycle.date_start && item.created <= cycle.date_finish && item.status !== 'arquivado',
    );

    return tasks?.length ? tasks[0] : null;
  }, [cycleActive, myTasks, participationTasks, taskCycles]);

  const canViewTabEquipe = useMemo(
    () =>
      !!playerInfo?.ro.match(/(3GUE|2GUT|2GCA|2GUA)/gi) ||
      !!playerInfo?.ro.match(/(2GUE|1GUT|1GUA|1GCA)/gi) ||
      playerInfo?.cod_funcao === 3002 ||
      playerInfo?.cod_funcao === 3003 ||
      playerInfo?.cod_funcao === 4005 ||
      playerInfo?.cod_funcao === 4007,
    [playerInfo],
  );

  // const isCurrentEvaluator = useCallback(
  //   (item: any) => {
  //     if (!user || !item || !playerInfo) return false;

  //     if (item.evaluator?.length) {
  //       const matriculaEvaluator = parseInt(
  //         item.evaluator[item.evaluator.length - 1].replace('F', '')
  //       );
  //       const currentId = parseInt(user.extra.id);

  //       return matriculaEvaluator === currentId;
  //     } else {
  //       return playerInfo.cod_etapa && playerInfo.cod_etapa > 0;
  //     }
  //   },
  //   [playerInfo, user]
  // );

  const evaluateTaskPerCycle: any = useMemo(() => {
    if (!Array.isArray(evaluateTasks) || !taskCycles?.length || !evaluateCycleActive) return null;

    const cycle = taskCycles.find((cycle: any) => cycle._id === evaluateCycleActive);

    const tasks = evaluateTasks.filter(item => item.created >= cycle.date_start && item.created <= cycle.date_finish);

    return tasks?.length ? tasks[tasks.length - 1] : null;

    // let tasksFiltered = tasks.filter((item: any) => !!isCurrentEvaluator(item));

    // if (tasksFiltered?.length) {
    //   tasksFiltered = tasksFiltered[tasksFiltered.length - 1];
    // } else {
    //   tasksFiltered = tasks[tasks.length - 1];
    // }

    // console.log(tasksFiltered);

    // return !!tasksFiltered ? tasksFiltered : null;
  }, [evaluateCycleActive, evaluateTasks, taskCycles]);

  const getActiveEvaluationStep: (0 | 2 | 3 | 4 | 4.1 | 5)[] | null = useMemo((): (0 | 2 | 3 | 4 | 4.1 | 5)[] | null => {
    if (!evaluateActiveCycleObj || !realDatetime) return null;

    if (
      realDatetime > evaluateActiveCycleObj.date_evaluation_step2_start &&
      realDatetime < evaluateActiveCycleObj.date_evaluation_step2_finish
    ) {
      // Etapa 2
      return [2];
    } else if (
      realDatetime > evaluateActiveCycleObj.date_evaluation_step3_start &&
      realDatetime < evaluateActiveCycleObj.date_evaluation_step4_finish
    ) {
      // Etapa 3
      return [4];
    } else if (
      realDatetime > evaluateActiveCycleObj.date_evaluation_step4_start &&
      realDatetime < evaluateActiveCycleObj.date_evaluation_step4_finish
    ) {
      // Etapa 4
      return [4];
    } else if (
      realDatetime > evaluateActiveCycleObj.date_evaluation_pitStop_start &&
      realDatetime < evaluateActiveCycleObj.date_evaluation_pitStop_finish
    ) {
      // Etapa 4.1
      return [4.1];
    } else if (
      realDatetime > evaluateActiveCycleObj.date_evaluation_step5_start &&
      realDatetime < evaluateActiveCycleObj.date_evaluation_step5_finish
    ) {
      // Etapa 5
      return [5];
    } else {
      return [0];
    }
  }, [evaluateActiveCycleObj, realDatetime]);

  const getPlayerStep = (task: any) => {
    if (!!task.prosseguir_idGestor && task.prosseguir_idGestor === playerInfo._id) {
      return 4.1;
    }

    if (typeof playerInfo.cod_etapa === 'number') {
      if (playerInfo.cod_etapa === 2) {
        return 2;
      }

      if (playerInfo.cod_etapa === 4) {
        return 4;
      }

      if (playerInfo.cod_etapa === 5) {
        return 5;
      }

      if (playerInfo.cod_etapa === 1 || playerInfo.cod_etapa === 3) {
        if (String(task.original_manager) === String(playerInfo._id)) {
          if (task.status === 'rejeitado' || task.status === 'aguardando') {
            return 2;
          } else {
            return 3;
          }
        } else {
          return 3;
        }
      }

      return 0;
    }

    return 0;
  };

  const htmlCountdownEvaluation = useMemo(() => {
    if (
      !evaluateActiveCycleObj ||
      (!getActiveEvaluationStep && getActiveEvaluationStep !== 0) ||
      !playerInfo ||
      !Array.isArray(evaluateTasks) ||
      !taskCycles?.length ||
      !evaluateCycleActive
    ) {
      setIsExperied(true);
      return <></>;
    }

    const codEtapa =
      playerInfo.cod_etapa === 2
        ? 2
        : playerInfo.cod_etapa === 1 || playerInfo.cod_etapa === 3
          ? 3
          : playerInfo.cod_etapa === 4
            ? 4
            : playerInfo.cod_etapa === 5
              ? 5
              : null;

    const now = +new Date();
    const months = taskCycles.filter((item: { type: string }) => item.type === 'month');
    const monthStage =
      months.find((item: { status: string }) => item.status === 'evaluation') ||
      months.find((item: { status: string }) => item.status === 'open');
    const nivel2 = taskCycles?.filter((item: { type: string }) => item.type === 'trimester');
    const nivel3 = taskCycles?.filter((item: { type: string }) => item.type === 'semester');
    const nivel2OuNivel3Stage =
      nivel2
        ?.sort((a: any, b: any) => a.date_evaluation_step4_start - b.date_evaluation_step4_start)
        ?.find((item: any) => {
          if (item.status === 'evaluation') {
            return true;
          }

          return (
            (item.date_evaluation_step4_start && now < item.date_evaluation_step4_start) ||
            (item.date_evaluation_pitStop_start && now < item.date_evaluation_pitStop_start)
          );
        }) ||
      nivel3
        ?.sort((a: any, b: any) => a.date_evaluation_step5_start - b.date_evaluation_step5_start)
        ?.find((item: any) => {
          if (item.status === 'evaluation') {
            return true;
          }

          return item.date_evaluation_step5_start && now < item.date_evaluation_step5_start;
        }) ||
      months.find((item: { status: string }) => item.status === 'evaluation');

    if (nivel2OuNivel3Stage?.type !== 'month' && nivel2OuNivel3Stage?.status === 'closed') {
      nivel2OuNivel3Stage.status = 'open';
    }

    const _evaluateActiveCycleObj = codEtapa === 4 || codEtapa === 5 ? nivel2OuNivel3Stage : monthStage;

    const cycle = taskCycles.find((cycle: any) => cycle._id === evaluateCycleActive);

    const tasks: any[] = evaluateTasks.filter(task => task.created >= cycle.date_start && task.created <= cycle.date_finish);
    if (_evaluateActiveCycleObj?.status === 'open') {
      setIsExperied(false);
      return (
        <span className="text-white font-thin">
          O período de avaliação desta fase inicia em:{' '}
          <strong className="font-semibold whitespace-nowrap">
            <Countdown
              key="missao"
              deadline={
                new Date(
                  codEtapa === 4
                    ? _evaluateActiveCycleObj.date_evaluation_step4_start
                    : codEtapa === 5
                      ? _evaluateActiveCycleObj.date_evaluation_step5_start
                      : _evaluateActiveCycleObj.date_finish,
                )
              }
            />
          </strong>
        </span>
      );
    } else if (_evaluateActiveCycleObj?.status === 'evaluation') {
      if (tasks.length) {
        const userTaskStep: number[] = tasks
          .map(task => (typeof getPlayerStep(task) === 'number' ? (getPlayerStep(task) as number) : 0))
          .sort(() => -1);

        const isStepIncluded = getActiveEvaluationStep
          ? getActiveEvaluationStep.some(step => userTaskStep.includes(step))
          : false;

        if (isStepIncluded) {
          const date_finish = getActiveEvaluationStep.includes(2)
            ? _evaluateActiveCycleObj?.date_evaluation_step2_finish
            : getActiveEvaluationStep.includes(3)
              ? _evaluateActiveCycleObj?.date_evaluation_step3_finish
              : getActiveEvaluationStep.includes(4)
                ? _evaluateActiveCycleObj?.date_evaluation_step4_finish
                : getActiveEvaluationStep.includes(4.1)
                  ? _evaluateActiveCycleObj?.date_evaluation_pitStop_finish
                  : _evaluateActiveCycleObj?.date_evaluation_step5_finish;

          setIsExperied(false);

          return (
            <span className="text-white font-thin">
              O período de apreciação deste nível termina em:{' '}
              <strong className="font-semibold whitespace-nowrap">
                <Countdown key="missao" deadline={new Date(date_finish)} />
              </strong>
            </span>
          );
        } else {
          const hasAfter = getActiveEvaluationStep
            ? userTaskStep?.find(x => getActiveEvaluationStep.some(step => x > step))
            : undefined;

          const date_start =
            hasAfter === 2
              ? _evaluateActiveCycleObj?.date_evaluation_step2_start
              : hasAfter === 3
                ? _evaluateActiveCycleObj?.date_evaluation_step3_start
                : hasAfter === 4
                  ? _evaluateActiveCycleObj?.date_evaluation_step4_start
                  : hasAfter === 4.1
                    ? _evaluateActiveCycleObj?.date_evaluation_pitStop_start
                    : _evaluateActiveCycleObj?.date_evaluation_step5_start;

          if (!!hasAfter) {
            setIsExperied(false);
            setIsStarting(true);
            return (
              <span className="text-white font-thin">
                O período de apreciação começa em:{' '}
                <strong className="font-semibold whitespace-nowrap">
                  <Countdown key="missao" deadline={new Date(date_start)} />
                </strong>
              </span>
            );
          } else {
            setIsExperied(true);
            return <></>;
          }
        }
      } else {
        const codEtapa =
          playerInfo.cod_etapa === 2
            ? 2
            : playerInfo.cod_etapa === 1 || playerInfo.cod_etapa === 3
              ? 3
              : playerInfo.cod_etapa === 4
                ? 4
                : playerInfo.cod_etapa === 5
                  ? 5
                  : null;

        if (codEtapa) {
          if (getActiveEvaluationStep.includes(codEtapa)) {
            const date_finish = getActiveEvaluationStep.includes(2)
              ? _evaluateActiveCycleObj?.date_evaluation_step2_finish
              : getActiveEvaluationStep.includes(3)
                ? _evaluateActiveCycleObj?.date_evaluation_step3_finish
                : getActiveEvaluationStep.includes(4)
                  ? _evaluateActiveCycleObj?.date_evaluation_step4_finish
                  : _evaluateActiveCycleObj?.date_evaluation_step5_finish;

            setIsExperied(false);
            return (
              <span className="text-white font-thin">
                O período de apreciação deste nível termina em:{' '}
                <strong className="font-semibold whitespace-nowrap">
                  <Countdown key="missao" deadline={new Date(date_finish)} />
                </strong>
              </span>
            );
          } else {
            if (getActiveEvaluationStep?.some(step => step < codEtapa)) {
              const date_start =
                codEtapa === 2
                  ? _evaluateActiveCycleObj?.date_evaluation_step2_start
                  : codEtapa === 3
                    ? _evaluateActiveCycleObj?.date_evaluation_step3_start
                    : codEtapa === 4
                      ? _evaluateActiveCycleObj?.date_evaluation_step4_start
                      : _evaluateActiveCycleObj?.date_evaluation_step5_start;
              setIsExperied(false);
              setIsStarting(true);
              return (
                <span className="text-white font-thin">
                  O período de apreciação começa em:{' '}
                  <strong className="font-semibold whitespace-nowrap">
                    <Countdown key="missao" deadline={new Date(date_start)} />
                  </strong>
                </span>
              );
            } else {
              setIsExperied(true);
              return <></>;
            }
          }
        } else {
          setIsExperied(true);
          return <></>;
        }
      }
    } else {
      setIsExperied(true);
      return <></>;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluateActiveCycleObj, evaluateCycleActive, evaluateTasks, getActiveEvaluationStep, playerInfo, taskCycles]);

  // Fallback local, usado só enquanto o aggregate não respondeu (ou falhou).
  //
  // Atenção ao tipo: a home é alimentada pelo `tasks_manager_qtd`, que projeta `evaluator`
  // e `original_manager` CRUS — arrays/strings de matrícula, sem os `*_detail` que o
  // `tasks_manager` hidrata. Comparar `lastManager._id` aqui lia `undefined` em toda
  // realização e derrubava a lista inteira, que é como o gestor com centenas de pendências
  // acabava vendo "não há nenhuma". A dupla é um par não-ordenado, então checar se o
  // responsável está na MINHA dupla é equivalente ao antigo caminho inverso.
  const hasToEvaluateLocal = useMemo(() => {
    if (!Array.isArray(evaluateTasks)) {
      return false;
    }

    const tasks = evaluateTasks.filter((item: any) => {
      const lastManager = item?.evaluator?.length ? item.evaluator[item.evaluator.length - 1] : item.original_manager;
      const taskStep = getTaskStep(item);

      if (
        lastManager &&
        String(lastManager) !== String(playerInfo?._id) &&
        (!Array.isArray(playerInfo?.mad_dupla) ||
          !playerInfo.mad_dupla.some((md: any) => String(md._id) === String(lastManager)))
      ) {
        return false;
      }

      // if (getActiveEvaluationStep?.includes(2) && lastManager === playerInfo?._id && item.status !== 'aguardando') {
      //   return false;
      // }

      if (
        item.status === 'concluidoAuto' ||
        item.status === 'concluido' ||
        item.status === 'selo' ||
        item.status === 'valeu' ||
        item.status === 'mandou' ||
        item.status === 'rejeitado'
      ) {
        return false;
      }

      // if (
      //   getActiveEvaluationStep?.some(step => step > 2) && // Verifica se algum valor é maior que 2
      //   lastManager === playerInfo?._id &&
      //   (item.status === 'concluidoAuto' || item.status === 'concluido' || item.status === 'selo')
      // ) {
      //   return false;
      // }

      if (taskStep && !getActiveEvaluationStep?.includes(taskStep)) {
        return false;
      }

      return true;
    });

    return tasks.length > 0;
  }, [evaluateTasks, getActiveEvaluationStep, playerInfo]);

  // O aggregate manda quando disponível. O filtro local só enxerga a fase VISÍVEL
  // (`getActiveEvaluationStep`, derivada de um único estágio) e o recorte que o
  // `tasks_manager_qtd` trouxe; o aggregate percorre o ciclo inteiro, nível a nível.
  const hasToEvaluate = useMemo(
    () => (pendingApreciacao === null ? hasToEvaluateLocal : pendingApreciacao > 0),
    [hasToEvaluateLocal, pendingApreciacao],
  );

  const showHasEvaluated = useMemo(() => {
    // Com a contagem, "não há nada aguardando" é exatamente zero pendências. Não cruzamos
    // com `isExperied`/`isStarting`: ambos derivam da fase visível e apagavam a caixa de
    // quem tinha pendência num nível diferente do corrente.
    if (pendingApreciacao !== null) {
      return pendingApreciacao === 0;
    }

    return !isExperied && !hasToEvaluate && !isStarting;
  }, [hasToEvaluate, isExperied, isStarting, pendingApreciacao]);

  const showPendingAppreciation = useMemo(() => {
    if (pendingApreciacao !== null) {
      return pendingApreciacao > 0;
    }

    return !isExperied && hasToEvaluate && !isStarting;
  }, [hasToEvaluate, isExperied, isStarting, pendingApreciacao]);

  function getAgenda() {
    if (!playerInfo || !Array.isArray(taskCycles) || !taskCycles.length) return null;

    const now = new Date();
    const stages = taskCycles.filter(item => item.type === 'month');
    const agendaItem = stages.find(item => item.status === 'evaluation') || stages.find(item => item.status === 'open');
    const nivel2OuNivel3Stages = taskCycles?.filter(item => item.type === 'semester' || item.type === 'trimester');
    const nivel2OuNivel3 =
      nivel2OuNivel3Stages?.find(item => item.status === 'evaluation') ||
      stages.find(item => item.status === 'evaluation') ||
      (nivel2OuNivel3Stages?.filter(item => item.status === 'open').length
        ? nivel2OuNivel3Stages.filter(item => item.status === 'open')[
            nivel2OuNivel3Stages.filter(item => item.status === 'open').length - 1
          ]
        : nivel2OuNivel3Stages[0]);

    const isValidacao =
      agendaItem?.date_evaluation_step2_start && agendaItem?.date_evaluation_step2_finish
        ? {
            isActive: isWithinInterval(now, {
              start: new Date(agendaItem.date_evaluation_step2_start),
              end: new Date(agendaItem.date_evaluation_step2_finish),
            }),
            isPast: isPast(new Date(agendaItem.date_evaluation_step2_finish)),
            isFuture: isFuture(new Date(agendaItem.date_evaluation_step2_start)),
          }
        : null;

    const isNivel1 =
      agendaItem?.date_evaluation_step3_start && agendaItem?.date_evaluation_step3_finish
        ? {
            isActive: isWithinInterval(now, {
              start: new Date(agendaItem.date_evaluation_step3_start),
              end: new Date(agendaItem.date_evaluation_step3_finish),
            }),
            isPast: isPast(new Date(agendaItem.date_evaluation_step3_finish)),
            isFuture: isFuture(new Date(agendaItem.date_evaluation_step3_start)),
          }
        : null;

    const isNivel2 =
      nivel2OuNivel3?.date_evaluation_step4_start && nivel2OuNivel3?.date_evaluation_step4_finish
        ? {
            isActive: isWithinInterval(now, {
              start: new Date(nivel2OuNivel3.date_evaluation_step4_start),
              end: new Date(nivel2OuNivel3.date_evaluation_step4_finish),
            }),
            isPast: isPast(new Date(nivel2OuNivel3.date_evaluation_step4_finish)),
            isFuture: isFuture(new Date(nivel2OuNivel3.date_evaluation_step4_start)),
          }
        : nivel2OuNivel3?.date_evaluation_pitStop_start && nivel2OuNivel3?.date_evaluation_pitStop_finish
          ? {
              isActive: isWithinInterval(now, {
                start: new Date(nivel2OuNivel3.date_evaluation_pitStop_start),
                end: new Date(nivel2OuNivel3.date_evaluation_pitStop_finish),
              }),
              isPast: isPast(new Date(nivel2OuNivel3.date_evaluation_pitStop_finish)),
              isFuture: isFuture(new Date(nivel2OuNivel3.date_evaluation_pitStop_start)),
            }
          : null;

    const isNivel3 =
      nivel2OuNivel3?.date_evaluation_step5_start && nivel2OuNivel3?.date_evaluation_step5_finish
        ? {
            isActive: isWithinInterval(now, {
              start: new Date(nivel2OuNivel3.date_evaluation_step5_start),
              end: new Date(nivel2OuNivel3.date_evaluation_step5_finish),
            }),
            isPast: isPast(new Date(nivel2OuNivel3.date_evaluation_step5_finish)),
            isFuture: isFuture(new Date(nivel2OuNivel3.date_evaluation_step5_start)),
          }
        : null;

    const renderStageState = (stage: { isActive: boolean; isFuture: boolean } | null) => {
      const state = stage?.isActive ? 'em andamento' : stage?.isFuture ? 'ainda não iniciada' : '';
      return state ? <span className="sr-only">{`, ${state}`}</span> : null;
    };

    const formatDateRange = (start: number, end: number) => {
      return `De ${format(new Date(start), 'dd/MM', { locale: ptBR })} a ${format(new Date(end), 'dd/MM', { locale: ptBR })}`;
    };

    const calculateProgress = (start: number, end: number) => {
      const total = end - start;
      const elapsed = now.getTime() - start;
      const progress = Math.max(0, Math.min(100, (elapsed / total) * 100));
      return progress;
    };

    if (!agendaItem && (!nivel2OuNivel3 || !nivel2OuNivel3.length)) {
      return <></>;
    }

    return (
      <div className="bg-maastricht-blue rounded-2xl p-4 lg:p-6 space-y-4 w-full my-9">
        <h3 className="text-lg">Agenda de Apreciação</h3>

        <ul className="grid grid-cols-2 gap-x-3 gap-y-4 lg:grid-cols-4">
          <li className="w-full space-y-2">
            <div
              className={`flex items-center space-x-1 ${isValidacao?.isActive || isValidacao?.isPast ? 'text-white' : 'text-[#9DB5C9]'}`}
            >
              {isValidacao?.isFuture && <MdOutlineLock size={18} aria-hidden="true" />}
              <span>
                Validação
                {renderStageState(isValidacao)}
              </span>
            </div>

            {isValidacao?.isActive ? (
              <div className="w-full bg-white rounded-2xl h-[10px] overflow-hidden">
                <div
                  className="h-full bg-[#FF3E5A] rounded-2xl transition-all duration-1000 ease-out"
                  style={{
                    width: `${calculateProgress(
                      agendaItem.date_evaluation_step2_start,
                      agendaItem.date_evaluation_step2_finish,
                    )}%`,
                  }}
                ></div>
              </div>
            ) : (
              <div className={`w-full rounded-2xl h-[10px] ${isValidacao?.isPast ? 'bg-electric-blue' : 'bg-[#C7D3EB]'}`}></div>
            )}

            <p className={`${isValidacao?.isPast ? 'text-white' : 'text-[#FF8FA0]'} font-bold text-sm`}>
              {isValidacao?.isPast
                ? 'Finalizado'
                : agendaItem?.date_evaluation_step2_start && agendaItem?.date_evaluation_step2_finish
                  ? formatDateRange(agendaItem.date_evaluation_step2_start, agendaItem.date_evaluation_step2_finish)
                  : ''}
            </p>
          </li>

          <li className="w-full space-y-2">
            <div
              className={`flex items-center space-x-1 ${isNivel1?.isActive || isNivel1?.isPast ? 'text-white' : 'text-[#9DB5C9]'}`}
            >
              {isNivel1?.isFuture && <MdOutlineLock size={18} aria-hidden="true" />}
              <span>
                Nível 1
                {renderStageState(isNivel1)}
              </span>
            </div>

            {isNivel1?.isActive ? (
              <div className="w-full bg-white rounded-2xl h-[10px] overflow-hidden">
                <div
                  className="h-full bg-[#FF3E5A] rounded-2xl transition-all duration-1000 ease-out"
                  style={{
                    width: `${calculateProgress(
                      agendaItem.date_evaluation_step3_start,
                      agendaItem.date_evaluation_step3_finish,
                    )}%`,
                  }}
                ></div>
              </div>
            ) : (
              <div className={`w-full rounded-2xl h-[10px] ${isNivel1?.isPast ? 'bg-electric-blue' : 'bg-[#C7D3EB]'}`}></div>
            )}

            <p className={`${isNivel1?.isPast ? 'text-white' : 'text-[#FF8FA0]'} font-bold text-sm`}>
              {isNivel1?.isPast
                ? 'Finalizado'
                : agendaItem?.date_evaluation_step3_start && agendaItem?.date_evaluation_step3_finish
                  ? formatDateRange(agendaItem.date_evaluation_step3_start, agendaItem.date_evaluation_step3_finish)
                  : ''}
            </p>
          </li>

          <li className="w-full space-y-2">
            <div
              className={`flex items-center space-x-1 ${isNivel2?.isActive || isNivel2?.isPast ? 'text-white' : 'text-[#9DB5C9]'}`}
            >
              {isNivel2?.isFuture && <MdOutlineLock size={18} aria-hidden="true" />}
              <span>
                Nível 2
                {renderStageState(isNivel2)}
              </span>
            </div>

            {isNivel2?.isActive ? (
              <div className="w-full bg-white rounded-2xl h-[10px] overflow-hidden">
                <div
                  className="h-full bg-[#FF3E5A] rounded-2xl transition-all duration-1000 ease-out"
                  style={{
                    width: `${calculateProgress(
                      nivel2OuNivel3.date_evaluation_step4_start || nivel2OuNivel3.date_evaluation_pitStop_start,
                      nivel2OuNivel3.date_evaluation_step4_finish || nivel2OuNivel3.date_evaluation_pitStop_finish,
                    )}%`,
                  }}
                ></div>
              </div>
            ) : (
              <div className={`w-full rounded-2xl h-[10px] ${isNivel2?.isPast ? 'bg-electric-blue' : 'bg-[#C7D3EB]'}`}></div>
            )}

            <p className={`${isNivel2?.isPast ? 'text-white' : 'text-[#FF8FA0]'} font-bold text-sm`}>
              {isNivel2?.isPast
                ? 'Finalizado'
                : nivel2OuNivel3?.date_evaluation_step4_start && nivel2OuNivel3?.date_evaluation_step4_finish
                  ? formatDateRange(nivel2OuNivel3.date_evaluation_step4_start, nivel2OuNivel3.date_evaluation_step4_finish)
                  : nivel2OuNivel3?.date_evaluation_pitStop_start && nivel2OuNivel3?.date_evaluation_pitStop_finish
                    ? formatDateRange(nivel2OuNivel3.date_evaluation_pitStop_start, nivel2OuNivel3.date_evaluation_pitStop_finish)
                    : ''}
            </p>
          </li>

          <li className="w-full space-y-2">
            <div
              className={`flex items-center space-x-1 ${isNivel3?.isActive || isNivel3?.isPast ? 'text-white' : 'text-[#9DB5C9]'}`}
            >
              {isNivel3?.isFuture && <MdOutlineLock size={18} aria-hidden="true" />}
              <span>
                Nível 3
                {renderStageState(isNivel3)}
              </span>
            </div>

            {isNivel3?.isActive ? (
              <div className="w-full bg-white rounded-2xl h-[10px] overflow-hidden">
                <div
                  className="h-full bg-[#FF3E5A] rounded-2xl transition-all duration-1000 ease-out"
                  style={{
                    width: `${calculateProgress(
                      nivel2OuNivel3.date_evaluation_step5_start,
                      nivel2OuNivel3.date_evaluation_step5_finish,
                    )}%`,
                  }}
                ></div>
              </div>
            ) : (
              <div className={`w-full rounded-2xl h-[10px] ${isNivel3?.isPast ? 'bg-electric-blue' : 'bg-[#C7D3EB]'}`}></div>
            )}

            <p className={`${isNivel3?.isPast ? 'text-white' : 'text-[#FF8FA0]'} font-bold text-sm`}>
              {isNivel3?.isPast
                ? 'Finalizado'
                : nivel2OuNivel3?.date_evaluation_step5_start && nivel2OuNivel3?.date_evaluation_step5_finish
                  ? formatDateRange(nivel2OuNivel3.date_evaluation_step5_start, nivel2OuNivel3.date_evaluation_step5_finish)
                  : ''}
            </p>
          </li>
        </ul>
      </div>
    );
  }

  useEffect(() => {
    async function getUserInfo() {
      try {
        const _playerInfo: any = userInfo;

        if (_playerInfo && Array.isArray(cyclesExtended) && cyclesExtended.length && !!cycleSelected) {
          if (
            !!_playerInfo?.ro.match(/(3GUE|2GUT|2GCA|2GUA)/gi) ||
            !!_playerInfo?.ro.match(/(2GUE|1GUT|1GUA|1GCA)/gi) ||
            _playerInfo?.cod_funcao === 3002 ||
            _playerInfo?.cod_funcao === 3003 ||
            _playerInfo?.cod_funcao === 4005 ||
            _playerInfo?.cod_funcao === 4007
          ) {
            const tasksPending = await getTasksPendingApprovalParticipants();
            setTasksPendingApproval(tasksPending);
          }

          setPlayerInfo(_playerInfo);

          const playerCadMissao = _playerInfo.cad_missao && _playerInfo.cad_missao > 0 && !_playerInfo?.is_manager;
          // const playerTipoDeAcordo =
          //   !_playerInfo.tipo_de_acordo ||
          //   (typeof _playerInfo?.tipo_de_acordo === 'string' &&
          //     !!_playerInfo?.tipo_de_acordo &&
          //     !!_playerInfo?.tipo_de_acordo?.match(/HIBRIDO/gi));

          // setPredefinedAgreementType(
          //   typeof _playerInfo.cad_missao &&
          //     _playerInfo.cad_missao > 0 &&
          //     typeof _playerInfo?.tipo_de_acordo === 'string' &&
          //     !!_playerInfo?.tipo_de_acordo &&
          //     !!_playerInfo?.tipo_de_acordo?.match(/PREDEFINIDO/gi),
          // );
          setPredefinedAgreementType(false);
          // setCanRegister(playerCadMissao && playerTipoDeAcordo);
          setCanRegister(playerCadMissao);

          const cycleActive = cyclesExtended.find(x => x._id === cycleSelected?._id);
          const _currentCycle = !!cycleActive ? cycleActive._id : cyclesExtended[0]._id;
          const _stages = cyclesExtended?.find(x => x._id === _currentCycle)?.steps;
          setTaskCycles(_stages);

          if (_stages) {
            const currentCycle = _stages.find((item: any) => item.status === 'open' && item.type && item.type === 'month');
            setCycleActive(currentCycle?._id || _stages.find((item: any) => item.type && item.type === 'month')?._id);
          }

          // Pode cadastrar
          if (playerCadMissao && _stages) {
            const data = await getTasks(user?._id);

            const my_tasks = data.filter((item: any) => item.owner === user?._id);
            const other_tasks = data.filter((item: any) => item.owner !== user?._id);

            setMyTasks(my_tasks);
            setParticipationTasks(other_tasks);

            // const _totalTask = await getTotalTasks();
            // setTotalTask(_totalTask);
          }
        }

        setLoadingTasks(false);
      } catch (e) {
        console.log(e);
      }
    }

    if (
      user &&
      !playerInfo &&
      Array.isArray(taskManager) &&
      !!realDatetime &&
      !taskCycles &&
      Array.isArray(cyclesExtended) &&
      cyclesExtended.length &&
      !!cycleSelected
    ) {
      getUserInfo();
    }
  }, [canViewTabEquipe, playerInfo, taskManager, user, userInfo, realDatetime, taskCycles, cyclesExtended, cycleSelected]);

  useEffect(() => {
    async function getUserInfo() {
      const _playerInfo: any = userInfo;

      // Gestor — ou substituto sem nível próprio (assessor cod 0 com cod_etapa_sub), que
      // precisa da busca lateral para apreciar no lugar do substituído (A2, 2026-10-21).
      if (
        (_playerInfo.cod_etapa && _playerInfo.cod_etapa > 0 && _playerInfo.cod_etapa <= 5) ||
        (Array.isArray(_playerInfo?.mad_dupla) &&
          _playerInfo.mad_dupla.some((md: any) => typeof md.cod_etapa === 'number' && md.cod_etapa > 0 && md.cod_etapa <= 5)) ||
        isSubstitutoComNivel(_playerInfo)
      ) {
        const currentCycle = taskCycles.find((item: any) => item.status === 'evaluation');
        const cycleId = currentCycle?._id || taskCycles.find((item: any) => item.type && item.type === 'month')?._id;

        const evaluateStage = taskCycles?.find((item: any) => item._id === cycleId);

        if (evaluateStage) {
          const data = taskManager?.filter(
            (e: any) =>
              evaluateStage.date_start &&
              evaluateStage.date_finish &&
              e.created >= evaluateStage.date_start &&
              e.created <= evaluateStage.date_finish,
          );

          setEvaluateTasks(data);
        }
        setEvaluateCycleActive(cycleId);

        if (!loadingLateralManagers && !Array.isArray(lateralManagers)) {
          setLoadingLateralManagers(true);
          const managers = await findLateralMenagers();
          setLateralManagers(managers || []);
          setLoadingLateralManagers(false);
        }
      }
    }

    if (Array.isArray(taskManager) && taskCycles && userInfo) {
      getUserInfo();
    }
  }, [getActiveEvaluationStep, lateralManagers, loadingLateralManagers, taskCycles, taskManager, userInfo]);

  useEffect(() => {
    const storedItem = getStorageItem('participant_notifications_read');

    if (Array.isArray(tasksPendingApproval) && tasksPendingApproval.length) {
      let _showModal = false;

      if (Array.isArray(storedItem) && storedItem.length) {
        tasksPendingApproval.forEach(x => {
          if (!storedItem.find(y => y === x._id)) {
            _showModal = true;
          }
        });
      } else {
        _showModal = true;
      }

      if (_showModal) {
        showModal('tasks-pending-approval-participants-modal');
      }
    }
  }, [showModal, tasksPendingApproval]);

  useEffect(() => {
    removeStorageItem('manager_lateralidade');
  }, []);

  function getDescription() {
    return (
      <p>
        Você possui acordo individual do tipo pré-definido na Plataforma Conexão e por isso, não realiza o cadastramento, mas pode
        participar de ações de outras equipes, participando da premiação coletiva. <br />
        <br />
        Para premiação individual a pontuação final das Realizações Destaque será pelo Placar Consolidado da Plataforma Conexão.
      </p>
    );
  }

  // Cadastro e apreciação na mesma home: a Agenda de Apreciação fica só na área de apreciação e
  // uma linha separa as duas (showsBothHomeAreas). Com uma área só, cada uma segue como era.
  const bothAreas = showsBothHomeAreas({ canRegister, predefinedAgreementType, playerInfo, lateralManagers });
  // Substituto sem nível próprio (HOM-06): só o botão da lateralidade; os botões que navegam com o
  // id dele cairiam no gate da apreciação sem acesso.
  const ownTeamButtons = !isSubstituteWithoutOwnLevel(playerInfo);

  return (
    <>
      <style>{`
        @keyframes partialTaskPulse {
          0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(251, 253, 0, 0.4); }
          50% { transform: scale(1.1); box-shadow: 0 0 0 10px rgba(251, 253, 0, 0); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(251, 253, 0, 0); }
        }
      `}</style>
      <div className="flex flex-col">
        {!loadingTasks ? (
          <>
            <div className="space-y-6 flex flex-col w-full">
              {/* {!!taskPerCycle || !!evaluateTaskPerCycle ? (
                <>
                  <div className="text-white font-normal text-xl lg:text-2xl leading-none -mb-4 text-left">
                    Realizações Destaque
                  </div>
                  <p className="text-white text-xs lg:text-sm block font-thin">
                    Visualize suas Realizações destaque e acompanhe os status de apreciação.
                  </p>
                </>
              ) : null} */}

              {/* Cadastro */}
              {!!canRegister ? (
                <div>
                  {!!taskPerCycle ? (
                    <div>
                      <p className="text-base lg:text-base leading-none block mb-3 lg:mb-4 text-yellow">
                        {!!playerInfo?.cod_etapa && playerInfo?.cod_etapa > 0 ? 'Sua última' : 'Última'} Realização cadastrada
                      </p>

                      <div className="flex items-center justify-between flex-col lg:flex-row space-y-3 lg:space-y-0 lg:space-x-8">
                        <div className="flex-1 bg-maastricht-blue rounded-lg p-4 lg:p-6 text-sm space-y-3">
                          <p className="font-thin">
                            <strong className="font-normal">Nome da entrega:</strong> {taskPerCycle.title}
                          </p>
                          <p className="font-thin">
                            <strong className="font-normal">Status da entrega:</strong> {GetTaskLevelLabel(taskPerCycle.status)}
                          </p>
                        </div>

                        {activeCycleObj?.status === 'open' ? (
                          <>
                            <span className="text-white block lg:hidden text-xs font-thin pb-3">
                              A fase será finalizada em:{' '}
                              <strong className="font-semibold">
                                <Countdown key="missao" deadline={new Date(activeCycleObj?.date_finish)} />
                              </strong>
                            </span>

                            {/* PROGRESS */}
                            <div className="lg:w-[120%] mt-2 block lg:hidden">
                              <ProgressBar startDate={activeCycleObj?.date_start} endDate={activeCycleObj?.date_finish} />
                            </div>
                          </>
                        ) : null}
                      </div>

                      {activeCycleObj?.status === 'open' ? (
                        <>
                          <span className="text-white hidden lg:block mt-5 text-sm font-thin">
                            A fase será finalizada em:{' '}
                            <strong className="font-semibold">
                              <Countdown key="missao" deadline={new Date(activeCycleObj?.date_finish)} />
                            </strong>
                          </span>

                          {/* PROGRESS */}
                          <div className="lg:w-full mt-2 hidden lg:block">
                            <ProgressBar startDate={activeCycleObj?.date_start} endDate={activeCycleObj?.date_finish} />
                          </div>
                        </>
                      ) : null}

                      {!bothAreas && getAgenda()}

                      <div className="mt-9 flex flex-col lg:flex-row items-center justify-center space-y-3 lg:space-y-0 lg:space-x-6">
                        {(!myTasks || !myTasks?.length || myTasks?.length < 1) &&
                          user?.extra?.task !== false &&
                          activeCycleObj?.status === 'open' &&
                          !playerInfo?.visualizador && (
                            <div className="relative inline-block">
                              <button
                                className="text-alice-blue bg-slate-blue text-sm flex items-center justify-center font-normal lg:font-semibold uppercase ring-2 ring-slate-blue hover:ring hover:ring-white rounded w-auto px-3 lg:px-4 h-10"
                                onClick={() => navigate('/realizacao-destaque/cadastrar')}
                                aria-label="Cadastrar realização. Abre modal"
                              >
                                Cadastrar realização
                              </button>
                              {!!hasPartialTask && (
                                <Tippy
                                  content="Você possui uma realização em preenchimento. Clique no botão para continuar."
                                  placement="top"
                                >
                                  <div
                                    className="absolute -top-3 -right-3 bg-yellow rounded-full p-1 cursor-pointer"
                                    onClick={() => navigate('/realizacao-destaque/cadastrar')}
                                    style={{
                                      animation: 'partialTaskPulse 1.8s ease-in-out infinite',
                                    }}
                                  >
                                    <IoIosWarning size={18} className="text-oxford-blue" />
                                  </div>
                                </Tippy>
                              )}
                            </div>
                          )}

                        <button
                          className="text-alice-blue bg-slate-blue text-sm flex items-center justify-center font-normal lg:font-semibold uppercase ring-2 ring-slate-blue hover:ring hover:ring-white rounded w-auto px-3 lg:px-4 h-10"
                          onClick={() => navigate('/realizacao-destaque')}
                          aria-label="Acompanhar minhas realizações destaque"
                        >
                          Acompanhar realizações
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col md:flex-row md:space-x-16 justify-between space-y-4 lg:space-y-6  rounded-2xl lg:rounded-[20px]  w-full mb-9">
                        <div>
                          <h2 className="text-white font-normal text-xl lg:text-2xl leading-none mb-2 text-left" tabIndex={0}>
                            Realizações Destaque
                          </h2>

                          <p className="font-thin" tabIndex={0}>
                            Visualize suas Realizações Destaque e acompanhe os status de apreciação.
                          </p>

                          <>
                            {activeCycleObj?.status === 'open' ? (
                              <p
                                className="block mb-4 mt-6 font-normal text-base lg:text-lg text-yellow tracking-tight"
                                tabIndex={0}
                              >
                                Que tal cadastrar uma Realização Destaque nesta fase?
                              </p>
                            ) : (
                              <p
                                className="block mb-4 mt-6 font-normal text-base lg:text-lg text-yellow tracking-tight"
                                tabIndex={0}
                              >
                                Você não possui Realização Destaque cadastrada nesta fase.
                              </p>
                            )}
                          </>

                          {/* <p className="font-thin" tabIndex={0}>
                            Cadastre suas Realizações Destaque, seja reconhecido pela sua liderança e ganhe recompensas!{' '}
                            <b>Lembre-se:</b> você pode cadastrar 1 realizações por fase (mês) nesse ciclo {cycleSelected?.title}{' '}
                            e ela deve estar concluída.
                          </p> */}

                          {activeCycleObj?.status === 'open' && (
                            <p className="font-thin" tabIndex={0}>
                              Cadastre suas Realizações Destaque, seja reconhecido pela sua liderança e ganhe recompensas!{' '}
                              <b>Lembre-se:</b> você pode cadastrar 1 realização durante a fase{' '}
                              {getMonthNameFromString(activeCycleObj?.title)} nesse ciclo {cycleSelected?.title} e ela deve estar
                              concluída.
                            </p>
                          )}

                          {activeCycleObj?.status === 'open' && (
                            <>
                              <span className="text-white hidden lg:block mt-3 text-sm font-thin" tabIndex={0}>
                                A fase será finalizada em:{' '}
                                <strong className="font-semibold">
                                  <Countdown key="missao" deadline={new Date(activeCycleObj?.date_finish)} />
                                </strong>
                              </span>

                              {/* PROGRESS */}
                              <div className="lg:w-[120%] mt-2 hidden lg:block">
                                <ProgressBar startDate={activeCycleObj?.date_start} endDate={activeCycleObj?.date_finish} />
                              </div>
                            </>
                          )}
                        </div>

                        <div aria-hidden={true}>
                          <img src={ImgFeedback} className="max-w-[186px]" alt="" />
                        </div>
                      </div>

                      {!bothAreas && getAgenda()}

                      <div className="flex flex-col lg:flex-row items-center justify-center space-y-3 lg:space-y-0 lg:space-x-6">
                        {activeCycleObj?.status === 'open' ? (
                          <>
                            {user?.extra?.task !== false && !playerInfo?.visualizador && (
                              <div className="relative inline-block">
                                <button
                                  className="text-alice-blue bg-slate-blue text-sm flex items-center justify-center font-normal lg:font-semibold uppercase ring-2 ring-slate-blue hover:ring hover:ring-white rounded w-auto px-3 lg:px-4 h-10"
                                  onClick={() => navigate('/realizacao-destaque/cadastrar')}
                                  aria-label="Cadastrar realização. Abre modal"
                                >
                                  Cadastrar realização
                                </button>
                                {!!hasPartialTask && (
                                  <Tippy
                                    content="Você possui uma realização em preenchimento. Clique no botão para continuar."
                                    placement="top"
                                  >
                                    <div
                                      className="absolute -top-3 -right-3 bg-yellow rounded-full p-1 cursor-pointer"
                                      onClick={() => navigate('/realizacao-destaque/cadastrar')}
                                      style={{
                                        animation: 'partialTaskPulse 1.8s ease-in-out infinite',
                                      }}
                                    >
                                      <IoIosWarning size={18} className="text-oxford-blue" />
                                    </div>
                                  </Tippy>
                                )}
                              </div>
                            )}

                            <button
                              className={`text-white text-sm flex items-center justify-center font-normal lg:font-semibold uppercase hover:ring hover:ring-white ring-2 ring-slate-blue rounded w-auto px-3 lg:px-4 h-10`}
                              onClick={() => navigate('/realizacao-destaque')}
                              aria-label="Acompanhar minhas realizações destaque"
                            >
                              Acompanhar realizações
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className={`text-alice-blue bg-slate-blue text-sm flex items-center justify-center font-normal hover:ring hover:ring-white lg:font-semibold uppercase ring-2 ring-slate-blue rounded w-auto px-3 lg:px-4 h-10`}
                              onClick={() => navigate('/realizacao-destaque')}
                            >
                              Acompanhar realizações
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : !!predefinedAgreementType ? (
                <>
                  <div className="flex flex-col md:flex-row md:space-x-16 justify-between space-y-4 lg:space-y-6  rounded-2xl lg:rounded-[20px]  w-full mb-4">
                    <div>
                      <div className="text-white font-normal text-xl lg:text-2xl leading-none mb-2 text-left">
                        Realizações Destaque
                      </div>

                      <p className="font-thin">Visualize suas Realizações Destaque e acompanhe os status de apreciação.</p>

                      <div className="flex items-center space-x-2 my-3">
                        <AiOutlineWarning className="text-yellow" size={25} />
                        <b>Você possui acordo individual do tipo pré-definido na Plataforma Conexão.</b>
                        <Tippy
                          arrow={true}
                          allowHTML={true}
                          className="bg-black text-white font-thin rounded-xl p-2 tippy"
                          content={getDescription()}
                        >
                          <div>
                            <BiInfoCircle className="color-[#C2D2FF] text-base lg:text-2xl" />
                          </div>
                        </Tippy>
                      </div>

                      {activeCycleObj?.status === 'open' && (
                        <>
                          <span className="text-white hidden lg:block mt-5 text-sm font-thin">
                            A fase será finalizada em:{' '}
                            <strong className="font-semibold">
                              <Countdown key="missao" deadline={new Date(activeCycleObj?.date_finish)} />
                            </strong>
                          </span>

                          {/* PROGRESS */}
                          <div className="lg:w-[120%] mt-2 hidden lg:block">
                            <ProgressBar startDate={activeCycleObj?.date_start} endDate={activeCycleObj?.date_finish} />
                          </div>
                        </>
                      )}
                    </div>

                    <div>
                      <img src={ImgFeedback} className="max-w-[186px]" alt=" " />
                    </div>
                  </div>

                  {!bothAreas && getAgenda()}

                  <button
                    className={`w-auto md:w-[314px] mx-auto text-white text-sm flex items-center justify-center font-normal lg:font-semibold uppercase hover:ring hover:ring-white ring-2 ring-slate-blue bg-slate-blue rounded px-3 lg:px-4 h-10`}
                    onClick={() => navigate('/realizacao-destaque')}
                  >
                    Acompanhar Realizações Destaque
                  </button>
                </>
              ) : null}

              {/* Divisória entre cadastro e apreciação, só quando as duas áreas aparecem. */}
              {bothAreas && <hr className="border-t border-white border-opacity-20" aria-hidden="true" />}

              {/* Gestor — ou substituto sem nível próprio: o bloco abre só para dar o botão da
                  lateralidade (A2); sem realização própria ele cai no ramo "nada aguardando". */}
              {isAppreciationAreaVisible(playerInfo, lateralManagers) ? (
                <div>
                  {/* `hasToEvaluate` entra na condição porque a pendência pode existir sem que o
                      `tasks_manager_qtd` tenha trazido a realização — é o caso do parceiro de dupla
                      que só consta como `non_evaluator`. Sem isso ele cairia no ramo vazio, sem o
                      botão de apreciar. */}
                  {!!evaluateTaskPerCycle || hasToEvaluate ? (
                    <>
                      <div className="flex flex-col md:flex-row md:space-x-16 justify-between space-y-4 lg:space-y-6  rounded-2xl lg:rounded-[20px]  w-full mb-9">
                        <div>
                          <div className="text-white font-normal text-xl lg:text-2xl leading-none mb-2 text-left">
                            Realizações Destaque
                          </div>

                          {!showHasEvaluated && (
                            <p className="font-thin">
                              Aprecie as realizações da sua equipe e visualize as realizações que participa.
                            </p>
                          )}

                          {showPendingAppreciation && (
                            <p className="block mb-4 mt-6 font-normal text-base lg:text-lg text-yellow tracking-tight">
                              {getPendingAppreciationLabel(pendingApreciacao)}
                            </p>
                          )}

                          {showHasEvaluated ? (
                            <>
                              <p className="block mb-4 mt-6 font-normal text-base lg:text-lg text-yellow tracking-tight">
                                Não há nenhuma Realização Destaque aguardando apreciação
                              </p>
                              <p className="font-thin">
                                {ownTeamButtons
                                  ? 'Acesse o botão "Acompanhar Realizações" para visualizar as apreciações das fases e ciclos anteriores.'
                                  : 'Use o botão "Apreciar Realizações Lateralidade/Substituição" para apreciar no lugar do gestor que você substitui.'}
                              </p>
                            </>
                          ) : (
                            <p className="font-thin">
                              Acesse as Realizações Destaque da sua equipe e inicie a etapa de apreciação. <b>Lembre-se:</b> você
                              precisa apreciar as realizações da sua equipe antes do prazo expirar.
                            </p>
                          )}

                          <span className="block mt-3 text-sm">{htmlCountdownEvaluation}</span>
                        </div>

                        <div>
                          <img src={ImgFeedback} className="max-w-[186px]" alt=" " />
                        </div>
                      </div>

                      {getAgenda()}

                      <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 items-center justify-between md:space-x-4">
                        {ownTeamButtons && (
                          <button
                            className={`text-alice-blue bg-slate-blue text-sm flex items-center justify-center font-normal lg:font-semibold uppercase ring-2 ring-slate-blue rounded w-full px-3 lg:px-4 h-11 leading-tight`}
                            onClick={() => navigate('/minha-equipe/validar-apreciar-realizacao')}
                          >
                            APRECIAR REALIZAÇÕES
                          </button>
                        )}

                        {!!lateralManagers?.length && (
                          <button
                            className={`text-alice-blue bg-slate-blue text-sm flex items-center justify-center font-normal lg:font-semibold uppercase ring-2 ring-slate-blue rounded w-full px-3 lg:px-4 h-11 leading-tight`}
                            onClick={() => setShowModalLateral(true)}
                          >
                            Apreciar Realizações <br />
                            Lateralidade/Substituição
                          </button>
                        )}

                        {userInfo?.tipo_de_acordo === 'HIBRIDO' && (
                          <button
                            className={`text-white text-sm flex items-center justify-center font-normal lg:font-semibold uppercase hover:ring hover:ring-white ring-2 ring-slate-blue rounded w-full px-3 lg:px-4 h-11 leading-tight`}
                            onClick={() => navigate('/realizacao-destaque')}
                          >
                            Acompanhar MINHAS realizações
                          </button>
                        )}

                        {Array.isArray(tasksPendingApproval) && !!tasksPendingApproval.length && (
                          <button
                            className={`text-white text-xl flex items-center justify-center hover:ring hover:ring-white ring-2 ring-slate-blue rounded-full !ml-6 h-11 w-11 relative`}
                            onClick={() => showModal('tasks-pending-approval-participants-modal')}
                          >
                            <LuBell />
                            <span className="flex items-center justify-center text-xs bg-flame h-5 min-w-5 px-1 text-white text-center rounded-full absolute -top-1 -right-1">
                              {tasksPendingApproval.length}
                            </span>
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col md:flex-row md:space-x-16 justify-between space-y-4 lg:space-y-6  rounded-2xl lg:rounded-[20px]  w-full mb-9">
                        <div>
                          <div className="text-white font-normal text-xl lg:text-2xl leading-none mb-2 text-left">
                            Realizações Destaque
                          </div>

                          <p className="block mb-4 mt-6 font-normal text-base lg:text-lg text-yellow tracking-tight">
                            Não há nenhuma Realização Destaque aguardando apreciação
                          </p>

                          <p className="font-thin">
                            {ownTeamButtons
                              ? 'Acesse o botão "Acompanhar Realizações" para visualizar as apreciações das fases e ciclos anteriores.'
                              : 'Use o botão "Apreciar Realizações Lateralidade/Substituição" para apreciar no lugar do gestor que você substitui.'}
                          </p>

                          <span className="block mt-3 text-sm">{htmlCountdownEvaluation}</span>
                        </div>

                        <div>
                          <img src={ImgFeedback} className="max-w-[186px]" alt=" " />
                        </div>
                      </div>

                      {getAgenda()}

                      <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 items-center justify-center md:space-x-4">
                        {ownTeamButtons && (
                          <button
                            className={`text-alice-blue bg-slate-blue text-sm flex items-center justify-center font-normal lg:font-semibold uppercase ring-2 ring-slate-blue rounded w-auto px-3 lg:px-4 h-11 leading-tight`}
                            onClick={() => navigate('/minha-equipe/validar-apreciar-realizacao')}
                          >
                            Acompanhar realizações
                          </button>
                        )}

                        {!!lateralManagers?.length && (
                          <button
                            className={`text-alice-blue bg-slate-blue text-sm flex items-center justify-center font-normal lg:font-semibold uppercase ring-2 ring-slate-blue rounded w-auto px-3 lg:px-4 h-11 leading-tight`}
                            onClick={() => setShowModalLateral(true)}
                          >
                            Apreciar Realizações <br />
                            Lateralidade/Substituição
                          </button>
                        )}

                        {userInfo?.tipo_de_acordo === 'HIBRIDO' && (
                          <button
                            className={`text-white text-sm flex items-center justify-center font-normal lg:font-semibold uppercase hover:ring hover:ring-white ring-2 ring-slate-blue rounded w-auto px-3 lg:px-4 h-11 leading-tight`}
                            onClick={() => navigate('/realizacao-destaque')}
                          >
                            Acompanhar MINHAS realizações
                          </button>
                        )}

                        {Array.isArray(tasksPendingApproval) && !!tasksPendingApproval.length && (
                          <button
                            className={`text-white text-xl flex items-center justify-center hover:ring hover:ring-white ring-2 ring-slate-blue rounded-full !ml-6 h-11 w-11 relative`}
                            onClick={() => showModal('tasks-pending-approval-participants-modal')}
                          >
                            <LuBell />
                            <span className="flex items-center justify-center text-xs bg-flame h-5 min-w-5 px-1 text-white text-center rounded-full absolute -top-1 -right-1">
                              {tasksPendingApproval.length}
                            </span>
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : null}

              {/* {canRegister ? (
                    <></>
                  ) : (
                    <>
                      <p className="text-white text-sm block mb-6 font-thin text-center tracking-tight">
                        <strong className="font-semibold text-base lg:text-lg block mb-4 text-yellow">
                          Sua equipe ainda não cadastrou Realizações Destaque nesta fase
                        </strong>
                      </p>
                    </>
                  )} */}

              {/* <div className="flex justify-center items-center flex-col space-y-2 lg:space-y-8 bg-maastricht-blue-3 rounded-[20px] p-8 text-center mt-8 w-full"> */}
              {/* {taskCycles?.map((item: any) => {
            // const buttonColor =
            //   item.status === "open" ? "secondary" : "outline";
            return (
              <div className="flex items-center space-x-5" key={item._id}>
                <div className="bg-maastricht-blue rounded-md px-5 py-3 text-sm">
                  <p className="font-thin ">
                    <b className="font-normal">Nome da ação:</b> Protótipo para
                    o JogaJuntoBB
                  </p>
                  <p className="font-thin">
                    <b className="font-normal">Status da entrega:</b> Aguardando
                    validação
                  </p>
                </div>

                <Button
                  color="primary"
                  className="uppercase py-2 text-sm"
                  onClick={() => navigate("/realizacao-destaque")}
                >
                  {item.title} - {GetTaskStatusLabel(item.status)}
                </Button>
              </div>
            );
          })} */}

              {/* {!canRegister && (
            <Button color="outline" className="uppercase py-2 bg-yellow" onClick={() => navigate('/realizacao-destaque')}>
              1º Fase - Em validação
            </Button>
          )}

          {!canRegister && !tasks && (
            <Button className="uppercase py-2" onClick={() => navigate('/realizacao-destaque')}>
              2º Fase - cadastrar
            </Button>
          )}

          {!canRegister && !!tasks && (
            <Button
              onClick={() => navigate(`/realizacao-destaque`)}
              color="clear"
              className="uppercase py-2 bg-blue-light text-blue-dark"
            >
              <div className="flex flex-row items-center space-x-2">
                <span>Ver suas entregas</span>
                <BiEdit />
              </div>
            </Button>
          )} */}

              {/* {!!tasks && tasks.status === 'ok' && (
        <div className="py-2 px-3 rounded-full bg-green-700 ring-2 ring-white text-white">
          <div className="">
            <span>Entrega Reconhecida: </span> +{getTaskPoints(tasks)} pontos
          </div>
        </div>
      )} */}

              {/* {canRegister && (
            <Button
              color="clear"
              className="uppercase py-2 bg-yellow text-blue-dark"
              onClick={() => navigate("/minha-equipe/validar-apreciar-realizacao")}
            >
              Avaliar Entregas da Equipe
            </Button>
          )} */}
            </div>
          </>
        ) : (
          <div className="text-center text-lg p-2 px-4 border rounded-full mt-3">Carregando informações...</div>
        )}
      </div>

      {!!lateralManagers?.length && showModalLateral && (
        <SearchGestor
          closeModal={() => setShowModalLateral(false)}
          onSubmit={id => {
            setStorageItem('manager_lateralidade', id._id);
            setShowModalLateral(false);
            navigate('/minha-equipe/validar-apreciar-realizacao');
          }}
        />
      )}

      <TasksPendingApprovalParticipantsModal tasks={tasksPendingApproval} />
    </>
  );
}
